import { useState, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Button } from '@cropler/ui-kit';

import { Icon as CroplerIcon } from '@cropler/ui-kit';

import styles from './styles.module.scss';
import stylesDashboard from '../../styles/dashboard.module.scss';
import stylesCharts from './styles.module.scss';

import {
  useGetNdviGddMetricsQuery,
  useGetAirMetricsQuery,
  useGetLocationQuery,
  useGetSoilMetricsQuery,
  useGetSensorsSummaryQuery,
} from '../../store/api/sensors';
import {
  getChartsAirMetrics,
  getChartsNdviMetrics,
  getChartsSoilMetrics,
  getChartsVegetationMetrics,
} from '../../utils/charts';
import {
  ChartsTypeNav,
  ChartsPeriodNav,
  Chart,
  ChartRain,
  ChartsCustomNav,
  ChartsTypeNavCurrent,
  ChartsCustomFilter,
  ChartsCustomSettings,
  ChartSoil,
} from './index.jsx';
import { useViewport } from '../../hooks';
import { BreakpointTablet } from '../../constants/ui';
import { CustomCharts } from '../../constants/keys';
import { FormatPair, GetChartsMap, GetChartsPeriodsMap, ResolvePairMetricData } from './lib/index.jsx';
import { Loading } from '../index.jsx';
import { EmptyViewCustomCharts } from '../EmptyView/index.jsx';
import Panel from '../Panel/index.jsx';
import { WeatherForecast } from '../Weather/index.jsx';
import EmptyViewCharts from '../EmptyView/EmptyViewCharts.jsx';

const Charts = ({ t, showSoilData, sensorFullData }) => {
  const { widthViewport } = useViewport();
  const ChartsMap = useMemo(() => GetChartsMap(t), [t]);
  const ChartsPeriodsMap = useMemo(() => GetChartsPeriodsMap(t), [t]);
  const defaultPeriod = ChartsPeriodsMap.month.value;
  const [period, setPeriod] = useState(defaultPeriod);
  const [chartsType, setChartsType] = useState('weather'); // weather, crop, soil, custom
  // const [detailAir, setDetailAir] = useState(false);
  // const [detailTemperature, setDetailTemperature] = useState(false);
  const [bbchAiActive, setBbchAiActive] = useState(false);
  const [sensors, setSensors] = useState([]);
  const [customPanelActive, setCustomPanelActive] = useState(false);
  const [customEditPanelActive, setCustomEditPanelActive] = useState(false);
  const [customCharts, setCustomCharts] = useState(() => JSON.parse(localStorage.getItem(CustomCharts) || '[]'));
  const [chartsTypeCloseTrigger, setChartsTypeCloseTrigger] = useState(0);

  const { selectedSensor, selectedSecondarySensor } = useSelector((state) => state.sensors);

  // selected sensor
  const { data: sensor } = useGetLocationQuery(
    { id: selectedSensor },
    { skip: !selectedSensor, refetchOnMountOrArgChange: true },
  );
  const { data: ndviGddMetrics, isFetching: isNdviGddLoading } = useGetNdviGddMetricsQuery(
    { id: selectedSensor },
    { skip: !selectedSensor, refetchOnMountOrArgChange: true },
  );
  const { data: airMetrics, isFetching: isAirLoading } = useGetAirMetricsQuery(
    { id: selectedSensor },
    { skip: !selectedSensor, refetchOnMountOrArgChange: true },
  );

  const { data: soilSensorMetrics, isFetching: isSoilMetricsLoading } = useGetSoilMetricsQuery(
    { id: selectedSensor },
    { skip: !selectedSensor || !showSoilData, refetchOnMountOrArgChange: true },
  );

  // compared sensor
  const { data: sensorCompared } = useGetLocationQuery(
    { id: selectedSecondarySensor },
    { skip: !selectedSecondarySensor, refetchOnMountOrArgChange: true },
  );
  const { data: ndviGddMetricsCompared, isFetching: isNdviGddComparedLoading } = useGetNdviGddMetricsQuery(
    { id: selectedSecondarySensor },
    { skip: !selectedSecondarySensor, refetchOnMountOrArgChange: true },
  );
  const { data: airMetricsCompared, isFetching: isAirComparedLoading } = useGetAirMetricsQuery(
    { id: selectedSecondarySensor },
    { skip: !selectedSecondarySensor, refetchOnMountOrArgChange: true },
  );
  const { data: sensorFullDataCompared = [], isFetching: isDataComparedLoading } = useGetSensorsSummaryQuery(
    { location_id: selectedSecondarySensor, ordering: 'date' },
    { skip: !selectedSecondarySensor },
  );

  useEffect(() => {
    if (isNdviGddLoading || !ndviGddMetrics || ndviGddMetrics.length === 0) return;
    let isBbchData = false;
    ndviGddMetrics.map((item) => {
      if (!isBbchData && (item['bbch_ai.value'] || item['bbch_ai.value'] === 0)) {
        isBbchData = true;
      }
    });
    setBbchAiActive(isBbchData);
  }, [ndviGddMetrics, isNdviGddLoading]);

  // Sensors list
  useEffect(() => {
    if (!sensor) return;
    const list = [sensor.name];
    if (sensorCompared && selectedSecondarySensor) {
      list.push(sensorCompared.name);
    }
    setSensors(list);
  }, [sensor, sensorCompared, selectedSecondarySensor]);

  // GDD, NDVI, BBCH, Leaf Area
  const metricsNdvi = useMemo(() => {
    if (!sensor || !ndviGddMetrics || isNdviGddLoading || isNdviGddComparedLoading) {
      return null;
    }
    return getChartsNdviMetrics({
      metrics: ndviGddMetrics,
      metricsCompared: ndviGddMetricsCompared,
      period: 365,
      // sensor,
      sensorCompared: selectedSecondarySensor,
    });
  }, [
    sensor,
    ndviGddMetrics,
    ndviGddMetricsCompared,
    isNdviGddLoading,
    isNdviGddComparedLoading,
    selectedSecondarySensor,
  ]);

  // Temperature, Humidity, Dew point
  const metricsAir = useMemo(() => {
    if (!sensor || !airMetrics || isAirLoading || isAirComparedLoading) {
      return null;
    }

    return getChartsAirMetrics({
      metrics: airMetrics,
      metricsCompared: sensorCompared ? airMetricsCompared : null,
      period: 365,
      sensorCompared: selectedSecondarySensor,
    });
  }, [
    sensor,
    sensorCompared,
    airMetrics,
    airMetricsCompared,
    isAirLoading,
    isAirComparedLoading,
    selectedSecondarySensor,
  ]);

  // Leaf NDVI, Soil NDVI (without comparison)
  const metricsVegetation = useMemo(() => {
    if (!sensor || !sensorFullData || isDataComparedLoading) {
      return null;
    }
    return getChartsVegetationMetrics({
      metrics: sensorFullData,
      metricsCompared: sensorFullDataCompared,
      period: 365,
      sensorCompared: selectedSecondarySensor,
    });
  }, [sensor, sensorFullData, selectedSecondarySensor, isDataComparedLoading]);

  // Rain, Soil humidity, Soil temperature, Salinity (without comparison)
  const metricsSoil = useMemo(() => {
    if (!sensor || !soilSensorMetrics || isSoilMetricsLoading) {
      return null;
    }
    return getChartsSoilMetrics({
      metrics: soilSensorMetrics,
      period: 365,
    });
  }, [sensor, soilSensorMetrics, isSoilMetricsLoading]);

  // For filter (Add new custom chart)
  const metricsMap = useMemo(() => {
    const { temperature, humidity, dew } = metricsAir || {};
    const { gdd, ndvi, bbch, leafArea } = metricsNdvi || {};
    const { leafNdvi, soilNdvi } = metricsVegetation || {};
    const { rain, soilHumidity, soilTemperature, salinity } = metricsSoil || {};

    return [
      temperature && temperature.length > 0 && ChartsMap.temperature.id,
      humidity && humidity.length > 0 && ChartsMap.humidity.id,
      dew && dew.length > 0 && ChartsMap.dew.id,
      gdd && gdd.length > 0 && ChartsMap.gdd.id,
      ndvi && ndvi.length > 0 && ChartsMap.ndvi.id,
      bbch && bbch.length > 0 && ChartsMap.bbch.id,
      leafArea && leafArea.length > 0 && ChartsMap.leafArea.id,
      leafNdvi && leafNdvi.length > 0 && ChartsMap.leafNdvi.id,
      soilNdvi && soilNdvi.length > 0 && ChartsMap.soilNdvi.id,
      rain && rain.data?.length > 0 && ChartsMap.rain.id,
      ...(soilHumidity?.dataByDays
        ? Object.entries(soilHumidity.dataByDays)
            .filter(([, values]) => Array.isArray(values) && values.length > 0)
            .map(([id]) => id)
        : []),
      ...(soilTemperature?.dataByDays
        ? Object.entries(soilTemperature.dataByDays)
            .filter(([, values]) => Array.isArray(values) && values.length > 0)
            .map(([id]) => id)
        : []),
      ...(salinity?.dataByDays
        ? Object.entries(salinity.dataByDays)
            .filter(([, values]) => Array.isArray(values) && values.length > 0)
            .map(([id]) => id)
        : []),
    ].filter(Boolean);
  }, [metricsAir, metricsNdvi, metricsVegetation, metricsSoil]);

  const onChangePeriod = (e) => {
    setPeriod(e);
  };

  const onChangeType = (e) => {
    setChartsType(e.target.value);
    setChartsTypeCloseTrigger((prev) => prev + 1);
  };

  const onAddCustomChart = () => {
    setCustomCharts(JSON.parse(localStorage.getItem(CustomCharts) || '[]'));
    setCustomPanelActive(false);
  };

  const onEditCustomChart = () => {
    setCustomCharts(JSON.parse(localStorage.getItem(CustomCharts) || '[]'));
    setCustomEditPanelActive(false);
  };

  const onOpenCustomFilter = () => {
    setCustomEditPanelActive(false);
    setCustomPanelActive(true);
  };

  const hasWeatherData = useMemo(
    () =>
      [metricsAir?.temperature, metricsNdvi?.gdd, metricsAir?.humidity, metricsAir?.dew, metricsSoil?.rain?.data].some(
        (series) => series && series.length > 0,
      ),
    [metricsAir, metricsNdvi, metricsSoil],
  );

  const hasCropData = useMemo(
    () =>
      [
        metricsNdvi?.leafArea,
        metricsNdvi?.ndvi,
        metricsNdvi?.bbch,
        metricsVegetation?.leafNdvi,
        metricsVegetation?.soilNdvi,
      ].some((series) => series && series.length > 0),
    [metricsNdvi, metricsVegetation],
  );

  const hasCustomChartsMetricsInMap = useMemo(() => {
    if (!Array.isArray(customCharts) || customCharts.length === 0) return false;
    if (!Array.isArray(metricsMap) || metricsMap.length === 0) return false;

    const metricsMapSet = new Set(metricsMap);

    return customCharts.some(
      (pair) => Array.isArray(pair) && pair.length > 0 && pair.every((metricId) => metricsMapSet.has(metricId)),
    );
  }, [customCharts, metricsMap]);

  if (!selectedSensor || isNdviGddLoading || isAirLoading || isSoilMetricsLoading || !metricsAir || !metricsNdvi) {
    return <Loading />;
  }

  return (
    <div className={`${styles.statisticsContainer} ${stylesDashboard.sensorContainerInner} charts custom-scroll`}>
      <div className={styles.statisticsNav}>
        {/*Type*/}
        <ChartsCustomNav
          header={<ChartsTypeNavCurrent value={chartsType} t={t} />}
          closeTrigger={chartsTypeCloseTrigger}
          body={
            <ChartsTypeNav onChangeType={onChangeType} defaultValue={chartsType} showSoilData={showSoilData} t={t} />
          }
        />
        {/*Period*/}
        {widthViewport > BreakpointTablet && (
          <ChartsPeriodNav onChangePeriod={onChangePeriod} defaultPeriod={defaultPeriod} t={t} />
        )}
      </div>

      {/*Weather*/}
      {chartsType === 'weather' &&
        (hasWeatherData ? (
          <>
            {/*Air Temperature*/}
            {metricsAir.temperature && metricsAir.temperature.length > 0 && (
              <Chart data={[metricsAir.temperature]} sensors={sensors} charts={['temperature']} period={period} t={t} />
            )}
            {/*GDD*/}
            {metricsNdvi.gdd && metricsNdvi.gdd.length > 0 && (
              <Chart data={[metricsNdvi.gdd]} sensors={sensors} charts={['gdd']} period={period} t={t} />
            )}
            {/*Humidity*/}
            {metricsAir.humidity && metricsAir.humidity.length > 0 && (
              <Chart data={[metricsAir.humidity]} sensors={sensors} charts={['humidity']} period={period} t={t} />
            )}
            {/*Dew point*/}
            {metricsAir.dew && metricsAir.dew.length > 0 && (
              <Chart data={[metricsAir.dew]} sensors={sensors} charts={['dew']} period={period} t={t} />
            )}
            {/*Rain chart*/}
            {metricsSoil && metricsSoil.rain && metricsSoil.rain.data?.length > 0 && (
              <ChartRain period={period} data={metricsSoil.rain} chartsMap={ChartsMap} t={t} />
            )}
          </>
        ) : (
          <EmptyViewCharts t={t} title={t('charts.noData.title')} message={t('charts.noData.text')} />
        ))}

      {/*Crop*/}
      {chartsType === 'crop' &&
        (hasCropData ? (
          <>
            {/*Leaf Area*/}
            {metricsNdvi.leafArea && metricsNdvi.leafArea.length > 0 && (
              <Chart data={[metricsNdvi.leafArea]} sensors={sensors} charts={['leafArea']} period={period} t={t} />
            )}
            {metricsVegetation && (
              <>
                {/*Leaf NDVI*/}
                {metricsVegetation.leafNdvi && metricsVegetation.leafNdvi.length > 0 && (
                  <Chart
                    data={[metricsVegetation.leafNdvi]}
                    sensors={sensors}
                    charts={['leafNdvi']}
                    period={period}
                    t={t}
                  />
                )}
                {/*Soil NDVI*/}
                {metricsVegetation.soilNdvi && metricsVegetation.soilNdvi.length > 0 && (
                  <Chart
                    data={[metricsVegetation.soilNdvi]}
                    sensors={sensors}
                    charts={['soilNdvi']}
                    period={period}
                    t={t}
                  />
                )}
              </>
            )}
            {/*Photo NDVI*/}
            {metricsNdvi.ndvi && metricsNdvi.ndvi.length > 0 && (
              <Chart data={[metricsNdvi.ndvi]} sensors={sensors} charts={['ndvi']} period={period} t={t} />
            )}
            {/*BBCH*/}
            {bbchAiActive && metricsNdvi.bbch && metricsNdvi.bbch.length > 0 && (
              <Chart data={[metricsNdvi.bbch]} sensors={sensors} charts={['bbch']} period={period} t={t} />
            )}
          </>
        ) : (
          <EmptyViewCharts t={t} title={t('charts.noData.title')} message={t('charts.noData.text')} />
        ))}

      {/*Soil*/}
      {showSoilData && chartsType === 'soil' && !!metricsSoil && (
        <>
          {/*Soil Moisture*/}
          {metricsSoil.soilHumidity && (
            <ChartSoil
              chartId={ChartsMap.soilHumidity.id}
              period={period}
              data={metricsSoil.soilHumidity.data}
              deep={metricsSoil.soilHumidity.selectedDeep}
              count={metricsSoil.soilHumidity.count}
              sensors={sensors}
              chartsMap={ChartsMap}
              t={t}
            />
          )}
          {/*Weather forecast*/}
          <div className={stylesCharts.statisticsPanel}>
            {sensorFullData && <WeatherForecast location={sensorFullData[0].location} t={t} />}
          </div>
          {/*Soil Temperature chart*/}
          {metricsSoil.soilTemperature && (
            <ChartSoil
              chartId={ChartsMap.soilTemperature.id}
              period={period}
              data={metricsSoil.soilTemperature.data}
              deep={metricsSoil.soilTemperature.selectedDeep}
              count={metricsSoil.soilTemperature.count}
              sensors={sensors}
              chartsMap={ChartsMap}
              t={t}
            />
          )}
          {/*Soil Salinity chart*/}
          {metricsSoil.salinity && metricsSoil.salinity.data?.length > 0 && !metricsSoil.salinity.isNoData && (
            <ChartSoil
              chartId={ChartsMap.salinity.id}
              period={period}
              data={metricsSoil.salinity.data}
              deep={metricsSoil.salinity.selectedDeep}
              count={metricsSoil.salinity.count}
              chartsMap={ChartsMap}
              t={t}
            />
          )}
        </>
      )}

      {/*Custom*/}
      {chartsType === 'custom' && (
        <>
          {/*Custom nav*/}
          {customCharts.length > 0 && hasCustomChartsMetricsInMap && (
            <div className={styles.chartNav}>
              <Button type="secondary" onClick={() => setCustomEditPanelActive(true)}>
                <CroplerIcon name="edit" />
                Manage
              </Button>
              <Button type="primary" onClick={() => setCustomPanelActive(true)} icon={<CroplerIcon name="add" />} />
            </div>
          )}

          {/*Empty View*/}
          {(!customCharts || customCharts.length === 0 || !hasCustomChartsMetricsInMap) && (
            <EmptyViewCustomCharts clickCreate={() => setCustomPanelActive(true)} t={t} />
          )}

          {/*Custom charts*/}
          {customCharts &&
            customCharts.length > 0 &&
            customCharts.map((pair, index) => {
              const data = pair
                .map((id) => ResolvePairMetricData(id, { metricsAir, metricsNdvi, metricsVegetation, metricsSoil }))
                .filter(Boolean);
              if (!data || data.length !== 2 || data[0].length === 0 || data[1].length === 0) return null;

              const { formatted, depth } = FormatPair(pair);
              return (
                <Chart
                  key={index}
                  data={data}
                  sensors={sensors}
                  charts={formatted}
                  period={period}
                  depth={depth}
                  t={t}
                />
              );
            })}

          {/*Custom filter*/}
          {customPanelActive && (
            <Panel
              title={t('charts.custom.createTitle')}
              setActivePanel={() => {
                setCustomPanelActive(false);
              }}
              isNoMainMenu={true}
            >
              <ChartsCustomFilter
                metricsMap={metricsMap}
                showSoilData={showSoilData}
                showBbchData={bbchAiActive}
                chartsMap={ChartsMap}
                countOfDepths={
                  metricsSoil && metricsSoil.soilHumidity?.selectedDeep?.length > 0
                    ? metricsSoil.soilHumidity.selectedDeep
                    : 0
                }
                saveChart={() => onAddCustomChart()}
                t={t}
              />
            </Panel>
          )}

          {/*Manage custom charts*/}
          {customEditPanelActive && (
            <Panel
              title={t('charts.custom.manageTitle')}
              setActivePanel={() => {
                setCustomEditPanelActive(false);
              }}
              isNoMainMenu={true}
            >
              <ChartsCustomSettings
                metricsMap={metricsMap}
                chartsMap={ChartsMap}
                saveChart={() => onEditCustomChart()}
                createChart={() => onOpenCustomFilter()}
                t={t}
              />
            </Panel>
          )}
        </>
      )}
    </div>
  );
};

export default Charts;
